import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useApi, useAction } from '../lib/hooks.js';
import { Loading, ErrorNote, Empty, Pill, ExpiryPill, PresencePill, relativeDate, formatQty } from '../components/ui.jsx';
import FoodIcon from '../components/FoodIcon.jsx';

export default function Dashboard() {
  const { data, error, loading, reload } = useApi('/dashboard');

  if (loading && !data) return <Loading rows={4} />;
  if (error) return <ErrorNote error={error} onRetry={reload} />;
  if (!data) return null;

  const { counts, expiring, low_stock: lowStock, stale, shopping_list: list, recent_activity: activity } = data;
  const needsAttention = counts.draft_receipts + counts.pending_scans;

  return (
    <div className="stack-lg">
      {needsAttention > 0 && (
        <div className="alert info">
          {counts.draft_receipts > 0 && (
            <div>
              <Link to="/capture">
                {counts.draft_receipts} receipt{counts.draft_receipts === 1 ? '' : 's'} waiting for review
              </Link>
            </div>
          )}
          {counts.pending_scans > 0 && (
            <div>
              <Link to="/capture">
                {counts.pending_scans} shelf check-up{counts.pending_scans === 1 ? '' : 's'} waiting to be saved
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Below the alert on purpose. A draft receipt waiting for review is a
          thing to do right now; the tour is a thing to do eventually, and the
          one that is already half-finished should not be pushed off screen by
          the one that has not been started. */}
      <GettingStarted counts={counts} />

      <div className="stat-grid">
        <Link className="stat" to="/inventory">
          <div className="value">{counts.inventory_items}</div>
          <div className="label">in stock</div>
        </Link>
        <Link className="stat" to="/items">
          <div className="value">{counts.master_items}</div>
          <div className="label">known items</div>
        </Link>
        <Link className="stat" to="/recipes">
          <div className="value">{counts.recipes}</div>
          <div className="label">recipes</div>
        </Link>
        <Link className="stat" to="/shopping">
          <div className="value">{list?.remaining ?? 0}</div>
          <div className="label">to buy</div>
        </Link>
      </div>

      <div className="row wrap" style={{ gap: 10 }}>
        <Link className="button primary grow" to="/capture">Scan something</Link>
        <Link className="button grow" to="/cook-now">What can I cook?</Link>
      </div>

      {expiring.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Use these soon</h2>
            <span className="count">{expiring.length}</span>
          </div>
          <div className="card list">
            {expiring.slice(0, 8).map((item) => (
              <div key={item.id} className="list-item">
                <FoodIcon name={item.item_name} category={item.category} imageUrl={item.image_url} />
                <div className="grow">
                  <div className="truncate">{item.item_name}</div>
                  <div className="small muted truncate">
                    {formatQty(item.quantity, item.unit)} · {item.location_name}
                  </div>
                </div>
                <ExpiryPill daysLeft={item.days_left} />
              </div>
            ))}
          </div>
        </section>
      )}

      {lowStock.length > 0 && (
        <section>
          <div className="section-title">
            <h2>Running low</h2>
            <Link className="count" to="/shopping">add to list</Link>
          </div>
          <div className="card list">
            {lowStock.map((item) => (
              <Link key={item.id} className="list-item" to={`/items/${item.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                <FoodIcon name={item.name} category={item.category} imageUrl={item.image_url} />
                <div className="grow truncate">{item.name}</div>
                <Pill tone={item.on_hand > 0 ? 'warn' : 'danger'}>
                  {/* formatQty, because on_hand is a float sum and 0.1 + 0.2
                      renders as 0.30000000000000004 without it. */}
                  {item.on_hand > 0 ? `${formatQty(item.on_hand)} left` : 'out'}
                </Pill>
              </Link>
            ))}
          </div>
        </section>
      )}

      {stale.length > 0 && <QuickCheckup rows={stale} onChanged={reload} />}

      <section>
        <div className="section-title"><h2>Recent activity</h2></div>
        {activity.length === 0 ? (
          <Empty title="Nothing yet">
            Scan a receipt or add something to your inventory to get started.
          </Empty>
        ) : (
          <div className="card list">
            {activity.map((event, index) => (
              <div key={index} className="list-item">
                <div className="grow">
                  <div className="truncate">
                    <span className="muted small">{event.event_type}</span>{' '}
                    {event.item_name || 'unknown item'}
                  </div>
                  <div className="tiny muted">
                    {relativeDate(event.created_at)}
                    {/* Only when somebody did it. An event with no actor came
                        from the system, and "by nobody" is worse than silence. */}
                    {event.actor_name && ` · ${event.actor_name}`}
                    {event.source !== 'manual' && ` · via ${event.source}`}
                  </div>
                </div>
                {event.quantity_delta != null && (
                  <span className={`small ${event.quantity_delta < 0 ? 'muted' : ''}`}>
                    {event.quantity_delta > 0 ? '+' : ''}{Math.round(event.quantity_delta * 100) / 100}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * M4/M7: two minutes a week, and most of a pantry stays true.
 *
 * The rows the app is least sure of, least sure first -- which is a different
 * list from the one this replaced. That one ranked by how long ago somebody
 * looked, so it opened with the salt nobody has touched since March and buried
 * the lettuce from a fortnight ago. Confidence decays on the category's own
 * clock, so the fortnight-old lettuce comes first and the salt may not appear
 * at all.
 *
 * Five at a time, because a check-up with twenty rows is a chore somebody
 * closes. Each answer is one tap and the row leaves the list, so the section
 * empties as it is worked through rather than needing a submit.
 */
function QuickCheckup({ rows, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [answered, setAnswered] = useState({});
  const { busy, error, run } = useAction();

  const shown = rows.filter((row) => !answered[row.id]).slice(0, expanded ? 10 : 5);
  if (shown.length === 0) return null;

  const answer = (row, here) => run(async () => {
    setAnswered((seen) => ({ ...seen, [row.id]: true }));
    try {
      if (here) await api.post(`/inventory/${row.id}/verify`, {});
      else await api.post(`/inventory/${row.id}/presence`, { state: 'out' });
    } catch (failed) {
      setAnswered((seen) => ({ ...seen, [row.id]: false }));
      throw failed;
    }
    // Reloaded once the answer is in, so the counts above agree with the list.
    await onChanged();
  });

  return (
    <section>
      <div className="section-title">
        <h2>Quick check-up</h2>
        <span className="count">{rows.length}</span>
      </div>
      <p className="small muted" style={{ margin: '0 0 8px' }}>
        Still on the shelf? A tap either way keeps the rest of the app honest.
      </p>
      <ErrorNote error={error} />
      <div className="card list">
        {shown.map((row) => (
          <div key={row.id} className="list-item" style={{ flexWrap: 'wrap', rowGap: 8 }}>
            <FoodIcon name={row.item_name} category={row.category} imageUrl={row.image_url} />
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="truncate">{row.item_name}</div>
              <div className="small muted truncate">
                {row.location_name}
                {row.days_since_verified !== null && ` · checked ${row.days_since_verified}d ago`}
              </div>
            </div>
            <PresencePill state={row.presence_state} />
            <div className="row" style={{ gap: 8 }}>
              <button type="button" className="small" disabled={busy} onClick={() => answer(row, true)}>
                Still here
              </button>
              <button type="button" className="small" disabled={busy} onClick={() => answer(row, false)}>
                Gone
              </button>
            </div>
          </div>
        ))}
      </div>
      {!expanded && rows.filter((row) => !answered[row.id]).length > 5 && (
        <button type="button" className="link small" style={{ marginTop: 6 }} onClick={() => setExpanded(true)}>
          show more
        </button>
      )}
    </section>
  );
}

/**
 * The tour, reduced to three lines, for somebody who has not finished it.
 *
 * The same three checks the /welcome page can make from real data -- food in,
 * a recipe saved, somebody else invited -- so the card and the tour can never
 * tell different stories about where you are. It reads the counts the dashboard
 * has already loaded; only the member count needs a request of its own, and
 * that one is skipped entirely unless this card is being drawn.
 *
 * Dismiss is the same call Finish makes, because it means the same thing: this
 * person does not want to be shown around. It reloads the flag rather than the
 * page, so the card leaves without the home screen blinking.
 */
function GettingStarted({ counts }) {
  const pending = useApi('/household/pending');
  const showing = pending.data?.onboarded === false;
  const household = useApi('/household/members', { skip: !showing });
  const { busy, error, run } = useAction();

  if (!showing) return null;

  const steps = [
    {
      to: '/capture',
      label: 'Add some food',
      done: counts.inventory_items > 0 || counts.master_items > 0,
    },
    { to: '/recipes/discover', label: 'Add a recipe', done: counts.recipes > 0 },
    {
      to: '/settings',
      label: 'Invite someone',
      done: (household.data?.members?.length ?? 0) > 1,
      loading: household.loading && !household.data,
    },
  ];

  return (
    <section className="card stack">
      <div className="row-between">
        <h2 style={{ fontSize: '1rem' }}>Getting started</h2>
        <button
          type="button"
          className="link small"
          disabled={busy}
          onClick={() => run(async () => {
            await api.post('/household/me/onboarded', {});
            // Awaited, so the button stays busy until the answer that removes
            // this card is actually in hand rather than merely asked for.
            await pending.reload();
          })}
        >
          dismiss
        </button>
      </div>

      <ErrorNote error={error} />

      <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {steps.map((step) => (
          <li key={step.to} className="row" style={{ gap: 10, minHeight: 28 }}>
            {/* Left looking like a link rather than dressed down to plain text:
                the point of the row is that it goes somewhere. */}
            <Link className="small grow truncate" to={step.to}>{step.label}</Link>
            {/* Blank while the read behind it is in flight: "not yet" is a
                verdict, and a verdict issued before the evidence arrives reads
                as the card flickering its mind. */}
            {step.loading
              ? null
              : step.done ? <Pill tone="ok">done</Pill> : <span className="tiny muted">not yet</span>}
          </li>
        ))}
      </ul>

      <div className="row">
        <Link className="button primary grow" to="/welcome">Continue the tour</Link>
      </div>
    </section>
  );
}
